import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import api, { authAPI } from '../utils/api';
import { toast } from 'sonner';
import { Sparkles, Eye, EyeOff, Loader2, ArrowLeft, Mail, Lock, CheckCircle2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import analytics from '../utils/analytics';
import { useRecaptcha } from '../hooks/useRecaptcha';
import { linkSessionToUser } from '../utils/growthAnalytics';
import { trackFunnel } from '../utils/funnelTracker';
import { markActivated } from '../utils/activationSentinel';
import { useGoogleLogin } from '@react-oauth/google';
import { useAppleSignIn } from '../hooks/useAppleSignIn';
import { safeRedirectPath } from '../utils/safeRedirect';

export default function Login({ setAuth }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotEmailError, setForgotEmailError] = useState('');
  const [sendingReset, setSendingReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [errors, setErrors] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [googleLoading, setGoogleLoading] = useState(false);
  const forgotEmailInputRef = useRef(null);
  const forgotPasswordLinkRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { executeRecaptcha } = useRecaptcha();

  // Preload app shell so /app renders faster after auth
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = '/app';
    document.head.appendChild(link);
    // Canonical activation funnel — login page mounted = signup_modal_opened
    const fromParam = searchParams.get('from') || 'direct';
    try {
      trackFunnel('signup_modal_opened', {
        source_page: 'login',
        meta: { from: fromParam, return: searchParams.get('return') },
      });
      // P1.7 — login_page_loaded with from=experience marks the post-checkout
      // intent funnel. Powers login_redirect_dropoff_pct.
      trackFunnel('login_page_loaded', {
        source_page: 'login',
        meta: { from: fromParam, paid_intent: fromParam === 'experience' },
      });
    } catch (_) { /* noop */ }
    return () => document.head.removeChild(link);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Focus email input when modal opens
  useEffect(() => {
    if (showForgotPassword && forgotEmailInputRef.current && !resetSent) {
      setTimeout(() => {
        forgotEmailInputRef.current?.focus();
      }, 100);
    }
  }, [showForgotPassword, resetSent]);

  // Email validation regex
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate forgot email field
  const validateForgotEmail = (value) => {
    const trimmed = value.trim();
    if (!trimmed) {
      return 'Email is required';
    }
    if (trimmed.length > 254) {
      return 'Email is too long';
    }
    if (!isValidEmail(trimmed)) {
      return 'Enter a valid email address';
    }
    return '';
  };

  // Validate email field
  const validateEmail = (value) => {
    const trimmed = value.trim();
    if (!trimmed) {
      return 'Email is required';
    }
    if (!isValidEmail(trimmed)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  // Validate password field  
  const validatePassword = (value) => {
    if (!value) {
      return 'Password is required';
    }
    if (value.length < 8) {
      return 'Password must be at least 8 characters';
    }
    return '';
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    // Clear errors when user starts typing
    if (errors.email) {
      setErrors(prev => ({ ...prev, email: '' }));
    }
    if (loginError) {
      setLoginError('');
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    // Clear errors when user starts typing
    if (errors.password) {
      setErrors(prev => ({ ...prev, password: '' }));
    }
    if (loginError) {
      setLoginError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous login error
    setLoginError('');
    
    // Validate all fields
    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);
    
    if (emailError || passwordError) {
      setErrors({ email: emailError, password: passwordError });
      return;
    }
    
    setLoading(true);

    // Canonical activation funnel event — user submitted credentials
    try {
      trackFunnel('signup_started', {
        source_page: 'login',
        meta: { method: 'email', from: searchParams.get('from') || 'direct' },
      });
    } catch (_) { /* never block auth */ }

    try {
      // Get reCAPTCHA token (always execute, backend enforces only after 3 failures)
      let captchaToken = '';
      if (failedAttempts >= 2) {
        captchaToken = await executeRecaptcha('login');
      }

      const response = await authAPI.login({ 
        email: email.trim().toLowerCase(), 
        password,
        captcha_token: captchaToken
      });
      localStorage.setItem('token', response.data.token);
      // Store user data from login response for immediate access
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
        localStorage.setItem('user_id', response.data.user.id || '');
        // Track login in Google Analytics
        analytics.trackLogin('email');
        analytics.setUserId(response.data.user.id);
        // Link anonymous session events to this user
        linkSessionToUser(response.data.user.id);
      }
      // Canonical activation funnel — successful auth
      try {
        trackFunnel('signup_success', {
          source_page: 'login',
          meta: { method: 'email', user_id: response.data.user?.id },
        });
        markActivated();
      } catch (_) { /* never block auth */ }
      setAuth(true);
      toast.success('Login successful!');
      // Priority: 1) URL ?next= / ?return= param (from 401 redirect),
      // 2) localStorage remix_return_url, 3) /app.
      // `?next=` is the canonical Billing-page contract (founder-mandated,
      // P0 2026-06). `?return=` remains accepted for backwards compat.
      // P0 2026-06 SECURITY — sanitize against open-redirect attacks.
      // Pinned: tests/test_safe_redirect_open_redirect_guard_2026_06.py
      const rawParam =
        searchParams.get('next') ||
        searchParams.get('return') ||
        localStorage.getItem('remix_return_url');
      if (rawParam && localStorage.getItem('remix_return_url')) {
        localStorage.removeItem('remix_return_url');
      }
      // safeRedirectPath returns the canonical `/app/dashboard` fallback
      // on any tampered value (per founder spec). If no rawParam at all,
      // land at `/app` (historical post-login default).
      const returnUrl = rawParam ? safeRedirectPath(rawParam) : '/app';
      navigate(returnUrl, { replace: true });
    } catch (error) {
      const status = error.response?.status;
      const message = error.response?.data?.detail || '';
      // Canonical activation funnel — failed auth
      try {
        trackFunnel('signup_failed', {
          source_page: 'login',
          meta: { method: 'email', status, error: String(message).slice(0, 200) },
        });
      } catch (_) { /* noop */ }
      
      // Handle account lockout (HTTP 423)
      if (status === 423) {
        const errorMsg = message || 'Account temporarily locked. Please try again later.';
        setLoginError(errorMsg);
        toast.error(errorMsg);
      }
      // Handle CAPTCHA requirement
      else if (status === 400 && message.toLowerCase().includes('captcha')) {
        setLoginError('Security verification required. Please try again.');
        setFailedAttempts(prev => Math.max(prev, 3));
      }
      // Handle remaining attempts warning
      else if (message.includes('attempts remaining')) {
        setFailedAttempts(prev => prev + 1);
        setLoginError(message);
        toast.error(message);
      }
      // Generic error message to not reveal if email exists
      else {
        setFailedAttempts(prev => prev + 1);
        const errorMsg = 'Invalid email or password. Please try again.';
        setLoginError(errorMsg);
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (sendingReset) return;
    
    // Validate email
    const emailError = validateForgotEmail(forgotEmail);
    if (emailError) {
      setForgotEmailError(emailError);
      return;
    }
    
    setSendingReset(true);
    
    try {
      // Get reCAPTCHA token for forgot password
      const captchaToken = await executeRecaptcha('forgot_password');
      
      const response = await authAPI.forgotPassword({ email: forgotEmail.trim().toLowerCase() }, {
        headers: { 'X-Captcha-Token': captchaToken }
      });
      if (response.data.success) {
        setResetSent(true);
        // Don't show specific toast - show generic success in modal
      }
    } catch (error) {
      // Still show success to prevent email enumeration
      setResetSent(true);
    } finally {
      setSendingReset(false);
    }
  };

  const handleForgotEmailChange = (e) => {
    const value = e.target.value;
    setForgotEmail(value);
    // Clear error when user starts typing
    if (forgotEmailError) {
      setForgotEmailError('');
    }
  };

  const handleModalClose = (open) => {
    if (!open) {
      // Reset modal state when closing
      setForgotEmailError('');
      setResetSent(false);
      // Return focus to the forgot password link
      setTimeout(() => {
        forgotPasswordLinkRef.current?.focus();
      }, 100);
    }
    setShowForgotPassword(open);
  };

  // Google Sign-In via popup (useGoogleLogin — access_token flow)
  const [googleClicking, setGoogleClicking] = useState(false);

  const handleGoogleSuccess = async (tokenResponse) => {
    setGoogleLoading(true);
    setGoogleClicking(false);
    try {
      const response = await api.post('/api/auth/google-signin', {
        access_token: tokenResponse.access_token,
      });
      const { token, user } = response.data;
      if (!token) throw new Error('No token in response');
      localStorage.setItem('token', token);
      if (user) {
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('user_id', user.id || '');
        analytics.trackLogin('google_direct');
        analytics.setUserId(user.id);
        linkSessionToUser(user.id);
      }
      // P0 activation funnel — canonical events
      try {
        trackFunnel('google_signin_success', {
          source_page: 'login',
          meta: { user_id: user?.id, returning: !!user?.created_at },
        });
        trackFunnel('signup_success', {
          source_page: 'login',
          meta: { method: 'google', user_id: user?.id },
        });
        markActivated();
      } catch (_) { /* never block auth */ }
      setAuth(true);
      // P0 2026-06 — accept `?next=` (canonical) or `?return=` (legacy).
      // P0 2026-06 SECURITY — sanitize against open-redirect attacks.
      const rawParam =
        searchParams.get('next') ||
        searchParams.get('return') ||
        localStorage.getItem('remix_return_url');
      if (rawParam && localStorage.getItem('remix_return_url')) {
        localStorage.removeItem('remix_return_url');
      }
      window.location.href = rawParam ? safeRedirectPath(rawParam) : '/app';
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Google sign-in failed. Please try again.';
      toast.error(msg);
      try {
        trackFunnel('google_signin_failed', {
          source_page: 'login',
          meta: { error: String(msg).slice(0, 200), status: error?.response?.status },
        });
      } catch (_) { /* noop */ }
      setGoogleLoading(false);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: (err) => {
      setGoogleClicking(false);
      toast.error('Google sign-in failed. Please try again.');
      try {
        trackFunnel('google_signin_failed', {
          source_page: 'login',
          meta: { error: String(err?.error || err?.message || 'oauth_error').slice(0, 200) },
        });
      } catch (_) { /* noop */ }
    },
    onNonOAuthError: (err) => {
      setGoogleClicking(false);
      toast.error('Google sign-in was cancelled.');
      try {
        // Detect popup-blocker vs user-closed
        const type = err?.type === 'popup_failed_to_open' ? 'google_popup_blocked' : 'google_popup_closed';
        trackFunnel(type, {
          source_page: 'login',
          meta: { type: err?.type || 'unknown' },
        });
      } catch (_) { /* noop */ }
    },
  });

  const handleGoogleClick = () => {
    if (googleClicking || googleLoading) return;
    setGoogleClicking(true);
    try {
      trackFunnel('google_signin_clicked', {
        source_page: 'login',
        meta: { from: searchParams.get('from') || 'direct' },
      });
    } catch (_) { /* noop */ }
    googleLogin();
  };

  // ── Sign in with Apple (web) — popup flow ──────────────────────────────
  // Mirrors handleGoogleSuccess: POST identity_token to the same backend
  // multi-audience endpoint used by the native iOS app and store the
  // returned `{ token, user }` exactly the same way.
  const [appleLoading, setAppleLoading] = useState(false);
  const [appleClicking, setAppleClicking] = useState(false);

  const handleAppleSuccess = async ({ identityToken, authorizationCode, fullName, email }) => {
    setAppleLoading(true);
    setAppleClicking(false);
    try {
      const response = await api.post('/api/auth/apple-signin', {
        identity_token: identityToken,
        authorization_code: authorizationCode || undefined,
        full_name: fullName || undefined,
        email: email || undefined,
      });
      const { token, user } = response.data;
      if (!token) throw new Error('No token in response');
      localStorage.setItem('token', token);
      if (user) {
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('user_id', user.id || '');
        analytics.trackLogin('apple_direct');
        analytics.setUserId(user.id);
        linkSessionToUser(user.id);
      }
      try {
        trackFunnel('apple_signin_success', {
          source_page: 'login',
          meta: { user_id: user?.id, returning: !!user?.created_at },
        });
        trackFunnel('signup_success', {
          source_page: 'login',
          meta: { method: 'apple', user_id: user?.id },
        });
        markActivated();
      } catch (_) { /* never block auth */ }
      setAuth(true);
      const rawParam =
        searchParams.get('next') ||
        searchParams.get('return') ||
        localStorage.getItem('remix_return_url');
      if (rawParam && localStorage.getItem('remix_return_url')) {
        localStorage.removeItem('remix_return_url');
      }
      window.location.href = rawParam ? safeRedirectPath(rawParam) : '/app';
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Apple sign-in failed. Please try again.';
      toast.error(msg);
      try {
        trackFunnel('apple_signin_failed', {
          source_page: 'login',
          meta: { error: String(msg).slice(0, 200), status: error?.response?.status },
        });
      } catch (_) { /* noop */ }
      setAppleLoading(false);
    }
  };

  const apple = useAppleSignIn({
    onSuccess: handleAppleSuccess,
    onError: (err) => {
      setAppleClicking(false);
      setAppleLoading(false);
      const code = String(err?.type || err?.error || 'apple_error');
      // Surface the actual Apple error code so misconfiguration (typo'd
      // Services ID, redirect_uri mismatch, unverified domain) is
      // diagnosable from the UI without digging into the console.
      const friendly = {
        apple_init_failed: 'Apple sign-in could not initialize. Check Services ID config.',
        apple_sdk_load_failed: 'Could not load Apple sign-in. Check your network / ad-blocker.',
        apple_not_ready: 'Apple sign-in not ready yet, please retry.',
        apple_no_id_token: 'Apple returned no identity token. Check Services ID + return URL.',
        invalid_client: 'Apple rejected the client_id. Verify REACT_APP_APPLE_SERVICES_ID matches the Services ID in Apple Developer Portal.',
        invalid_redirect_uri: 'Apple rejected the redirect URL. Verify REACT_APP_APPLE_REDIRECT_URI matches one of the Return URLs registered on the Services ID.',
        invalid_request: 'Apple rejected the sign-in request. Likely a redirect URL or domain-verification issue.',
        popup_closed_by_user: 'Sign-in cancelled.',
        user_cancelled_authorize: 'Sign-in cancelled.',
      }[code] || `Apple sign-in failed (${code}). Please try again.`;
      // Verbose log so the browser console always shows the raw error
      // even when the toast is dismissed.
      // eslint-disable-next-line no-console
      console.error('[apple-signin] error', { code, raw: err });
      toast.error(friendly);
      try {
        trackFunnel('apple_signin_failed', {
          source_page: 'login',
          meta: { error: code.slice(0, 200) },
        });
      } catch (_) { /* noop */ }
    },
    onCancel: () => {
      setAppleClicking(false);
      setAppleLoading(false);
    },
  });

  const handleAppleClick = () => {
    if (appleClicking || appleLoading || !apple.ready) return;
    setAppleClicking(true);
    try {
      trackFunnel('apple_signin_clicked', {
        source_page: 'login',
        meta: { from: searchParams.get('from') || 'direct' },
      });
    } catch (_) { /* noop */ }
    apple.signIn();
  };

  // Check if email is valid for enabling submit button
  const isForgotEmailValid = forgotEmail.trim() && isValidEmail(forgotEmail.trim());

  // Custom input styles for dark theme with proper alignment
  const inputBaseStyles = `
    w-full h-12 rounded-lg border border-slate-600/50 
    bg-slate-800/80 text-slate-100 
    placeholder:text-slate-400
    focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30 focus:outline-none
    transition-all duration-200
    text-base
  `.replace(/\s+/g, ' ').trim();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-indigo-950 to-slate-950 flex items-center justify-center p-4">
      {/* Loading overlay for Google sign-in */}
      {googleLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/90 backdrop-blur-sm">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-500 mx-auto mb-3" />
            <p className="text-white text-sm">Signing you in...</p>
          </div>
        </div>
      )}

      <div className="w-full max-w-md">
        <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-800 rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-4">
              <Sparkles className="w-8 h-8 text-indigo-500" />
              <span className="text-2xl font-bold text-white">Visionary Suite</span>
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">Welcome Back</h2>
            <p className="text-slate-400">Login to continue creating</p>
            {searchParams.get('from') === 'experience' && (
              <div
                className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-medium"
                data-testid="login-paid-intent-microcopy"
              >
                <Sparkles className="w-3 h-3" />
                One quick step to create your video securely
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300 text-sm font-medium block">
                Email
              </Label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none z-10">
                  <Mail className="w-5 h-5 text-slate-400" aria-hidden="true" />
                </span>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={() => setErrors(prev => ({ ...prev, email: validateEmail(email) }))}
                  aria-label="Email address"
                  aria-describedby={errors.email ? "email-error" : undefined}
                  aria-invalid={!!errors.email}
                  style={{ paddingLeft: '48px', paddingRight: '16px' }}
                  className={`${inputBaseStyles} ${errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' : ''}`}
                  placeholder="you@example.com"
                  data-testid="login-email-input"
                  autoComplete="email"
                />
              </div>
              {errors.email && (
                <p id="email-error" className="text-red-400 text-sm mt-1" role="alert">
                  {errors.email}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-slate-300 text-sm font-medium">
                  Password
                </Label>
                <button
                  ref={forgotPasswordLinkRef}
                  type="button"
                  onClick={() => {
                    setForgotEmail(email);
                    setForgotEmailError('');
                    setShowForgotPassword(true);
                    setResetSent(false);
                  }}
                  className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors focus:outline-none focus:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none z-10">
                  <Lock className="w-5 h-5 text-slate-400" aria-hidden="true" />
                </span>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={handlePasswordChange}
                  onBlur={() => setErrors(prev => ({ ...prev, password: validatePassword(password) }))}
                  aria-label="Password"
                  aria-describedby={errors.password ? "password-error" : undefined}
                  aria-invalid={!!errors.password}
                  style={{ paddingLeft: '48px', paddingRight: '48px' }}
                  className={`${inputBaseStyles} ${errors.password ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' : ''}`}
                  placeholder="Enter your password"
                  data-testid="login-password-input"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center justify-center text-slate-400 hover:text-slate-200 transition-colors focus:outline-none"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.password && (
                <p id="password-error" className="text-red-400 text-sm mt-1" role="alert">
                  {errors.password}
                </p>
              )}
            </div>

            {/* Inline Login Error Message */}
            {loginError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-3" data-testid="login-error-message">
                <div className="w-5 h-5 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-red-400 text-xs font-bold">!</span>
                </div>
                <p className="text-red-400 text-sm">{loginError}</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white rounded-xl py-6 text-lg shadow-lg shadow-indigo-500/20 disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-200"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Logging in...
                </>
              ) : (
                'Login'
              )}
            </Button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-slate-900/50 text-slate-400">Or continue with</span>
              </div>
            </div>

            <div className="w-full mt-4 flex justify-center" data-testid="google-signin-btn">
              <button
                type="button"
                onClick={handleGoogleClick}
                disabled={googleClicking || googleLoading}
                className="w-full max-w-[380px] h-11 rounded-full border border-slate-600 bg-slate-800 hover:bg-slate-700 transition-colors flex items-center justify-center gap-3 text-sm font-medium text-white disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="google-signin-popup-btn"
              >
                <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
                  <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
                  <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                  <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 6.29C4.672 4.163 6.656 2.58 9 2.58z" fill="#EA4335"/>
                </svg>
                Sign in with Google
              </button>
            </div>

            {apple.configured && (
              <div className="w-full mt-3 flex justify-center" data-testid="apple-signin-btn">
                <button
                  type="button"
                  onClick={handleAppleClick}
                  disabled={appleClicking || appleLoading || !apple.ready}
                  className="w-full max-w-[380px] h-11 rounded-full border border-slate-700 bg-black hover:bg-slate-900 transition-colors flex items-center justify-center gap-3 text-sm font-medium text-white disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="apple-signin-popup-btn"
                  aria-label="Continue with Apple"
                >
                  <svg width="16" height="18" viewBox="0 0 16 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path
                      d="M13.07 9.54c-.02-2.04 1.67-3.02 1.74-3.07-.95-1.39-2.43-1.58-2.96-1.6-1.26-.13-2.46.74-3.1.74-.65 0-1.63-.72-2.69-.7-1.38.02-2.66.81-3.37 2.05-1.44 2.5-.37 6.2 1.04 8.23.69.99 1.5 2.1 2.56 2.06 1.03-.04 1.42-.66 2.67-.66 1.25 0 1.6.66 2.69.64 1.11-.02 1.81-1 2.49-2 .78-1.14 1.1-2.25 1.12-2.31-.02-.01-2.14-.82-2.16-3.26zM11.07 3.51c.57-.69.95-1.65.85-2.6-.82.03-1.81.55-2.4 1.24-.53.61-.99 1.58-.87 2.52.91.07 1.85-.47 2.42-1.16z"
                      fill="currentColor"
                    />
                  </svg>
                  Continue with Apple
                </button>
              </div>
            )}
          </div>

          <div className="mt-6 text-center">
            <p className="text-slate-400">
              Don't have an account?{' '}
              <Link to="/signup" className="text-indigo-400 hover:text-indigo-300 font-medium transition-colors">
                Sign up
              </Link>
            </p>
          </div>
        </div>
        
        {/* Back to Home */}
        <div className="text-center mt-6">
          <Link to="/" className="text-slate-500 hover:text-slate-300 transition-colors inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>
      
      {/* Forgot Password Modal - Enhanced */}
      <Dialog open={showForgotPassword} onOpenChange={handleModalClose}>
        <DialogContent 
          className="bg-slate-900 border-slate-700 sm:max-w-md"
          aria-describedby="reset-password-description"
        >
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2 text-lg">
              <Lock className="w-5 h-5 text-indigo-400" aria-hidden="true" />
              Reset Password
            </DialogTitle>
            <DialogDescription id="reset-password-description" className="text-slate-400 text-sm">
              {resetSent 
                ? "If an account exists for that email, we sent a reset link."
                : "Enter your email address and we'll send you a link to reset your password."
              }
            </DialogDescription>
          </DialogHeader>
          
          {!resetSent ? (
            <form onSubmit={handleForgotPassword} className="space-y-4 py-2">
              <div className="space-y-2">
                <Label 
                  htmlFor="forgot-email" 
                  className="text-slate-300 text-sm font-medium block"
                >
                  Email Address
                </Label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none z-10">
                    <Mail className="w-5 h-5 text-slate-400" aria-hidden="true" />
                  </span>
                  <input
                    ref={forgotEmailInputRef}
                    id="forgot-email"
                    type="email"
                    value={forgotEmail}
                    onChange={handleForgotEmailChange}
                    onBlur={() => {
                      if (forgotEmail.trim()) {
                        setForgotEmailError(validateForgotEmail(forgotEmail));
                      }
                    }}
                    placeholder="you@example.com"
                    style={{ paddingLeft: '48px', paddingRight: '16px' }}
                    className={`${inputBaseStyles} ${forgotEmailError ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' : ''}`}
                    aria-label="Email address for password reset"
                    aria-describedby={forgotEmailError ? "forgot-email-error" : undefined}
                    aria-invalid={!!forgotEmailError}
                    maxLength={254}
                    data-testid="forgot-email-input"
                    autoComplete="email"
                  />
                </div>
                {forgotEmailError && (
                  <p id="forgot-email-error" className="text-red-400 text-sm" role="alert">
                    {forgotEmailError}
                  </p>
                )}
              </div>
              <DialogFooter className="gap-2 pt-4 flex-col-reverse sm:flex-row">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => handleModalClose(false)} 
                  className="border-slate-700 text-slate-300 hover:bg-slate-800 w-full sm:w-auto"
                  data-testid="forgot-password-cancel"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={sendingReset || !isForgotEmailValid} 
                  className="bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                  data-testid="forgot-password-submit"
                >
                  {sendingReset ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="py-6 text-center">
              <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-white font-medium text-lg mb-2">Check Your Email</h3>
              <p className="text-slate-400 text-sm mb-6">
                If an account exists for <span className="text-slate-300">{forgotEmail}</span>, you'll receive a password reset link shortly.
              </p>
              <p className="text-slate-500 text-xs mb-4">
                Didn't receive the email? Check your spam folder or try again.
              </p>
              <Button 
                onClick={() => handleModalClose(false)} 
                className="bg-indigo-500 hover:bg-indigo-600"
                data-testid="back-to-login-btn"
              >
                Back to Login
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
